/*
 * Copyright (c) 2024 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import { Add } from "@mui/icons-material"
import { Box, IconButton, Tooltip } from "@mui/material"
import { config } from "../../../../../../../AppConfig/config"
import { tableButtonGray } from "../../../../../../../constants/colors"
import { KEY_NAME_SOURCE_DATASET } from "../../../constants"
import {SCENARIO_ANALYSIS_CONSTANTS} from "../../../../../ScenarioAnalysis/constants"

export interface DrivingScenarioPropertiesRow {
    type: string,
    category?: string,
    name: string, 
    property_link?: string,
    objectId?: string,
    variantName?: string
    [KEY_NAME_SOURCE_DATASET]?: string[],
    subRows?: DrivingScenarioPropertiesRow[]
}

export interface DrivingScenarioDesignExperimentRow {
    enumValue: any[],
    variableType: string,
    variableName: string,
    min: number,
    max: number,
    rationale: string,
    totalSteps: any,
    sourceName: string
    units: string
    userVisible : string
    default: string
}
export interface UpstreamDependenciesRow{
  field: string
  contributors: string
  considered: string
}

export interface SourceDatasetsRow{
  name: string
  id: string
}
export const OBJECT_TYPE_STR = "Object"
export const ROAD_TYPE_TYPE_STR = "Road Type"
export const CAPABILITY_TYPE_STR = "Capability"
export const USE_CASE_TYPE_STR = "Use Case"
export const PACKAGE_TYPE_STR = "Package"
export const MODALITY_TYPE_STR = "Modality"
export const PERCEPTION_DATA_FLOW_TYPE_STR = "Perception Data Flow"
export const SAFETY_TYPE_STR = "Safety (ASIL Rating)"

const requiredPropertiesDefault = [
  OBJECT_TYPE_STR,
  ROAD_TYPE_TYPE_STR,
  CAPABILITY_TYPE_STR,
  USE_CASE_TYPE_STR, 
  PACKAGE_TYPE_STR,
  MODALITY_TYPE_STR, 
  PERCEPTION_DATA_FLOW_TYPE_STR, 
  SAFETY_TYPE_STR, 
] 

export const PROPERTY_TYPE_IS_MULTIPLE_SELECT = {
  [OBJECT_TYPE_STR]: true,
  [ROAD_TYPE_TYPE_STR]: true,
  [CAPABILITY_TYPE_STR]: false,
  [USE_CASE_TYPE_STR]: false,
  [PACKAGE_TYPE_STR]: false,
  [MODALITY_TYPE_STR]: false,
  [PERCEPTION_DATA_FLOW_TYPE_STR]: false,
  [SAFETY_TYPE_STR]: false
}
export interface ITitleObject{
  reportTitle: string,
  componentTitle: string
}
export const FULL_REPORT_SECTION_TITLES: ITitleObject[] = [
  {
    "reportTitle": "CONTENT SUB", 
    "componentTitle" : "REQUIREMENTS"
  },
  {
    "reportTitle" : "HORIZONTAL COVERAGE - GRAPH",
    "componentTitle" :"HORIZONTAL COVERAGE - GRAPH"
  }, 
  {
    "reportTitle" : "HORIZONTAL COVERAGE - DATA",
    "componentTitle" : "HORIZONTAL COVERAGE - DATA"
  },
  {
    "reportTitle" : "UPSTREAM ANALYSIS DEPENDENCIES",
    "componentTitle" : "UPSTREAM ANALYSIS DEPENDENCIES"
  }
]
const fetchRwoDataEndpoint ='/HttpTriggerCoverageRequirements?request_operation=get&request_type=getRwoData'
const fetchDatasetDataEndpoint = "/HttpTriggerGetDatasetData"
const fetchDatasetDataEndpointFullURL = `${config.url.API_URL}/HttpTriggerGetDatasetData?container=RedlineDatasets`
const datasetSaveData = "/HttpTriggerSaveDatasetData"
const drivingScenarioDetailsTable = SCENARIO_ANALYSIS_CONSTANTS.viewModel.endpoints.modelParameterList
// const runAnalysisOnDataset = "/orchestrators/DurableFunctionsOrchestrator2";
const runAnalysisOnDataset = "/orchestrators/DurableFunctionsHttpStart2";
const downloadDatasetAsCSV = "/HttpTriggerDownloadDatasetAsCSV"
const setBenchmarkSettingsUrl = "/HttpTriggerArgosBenchmarking?"
export const propertiesTableToolbarActions = (handleOpenDialog : any, setIsAddProperty: any) => {
  return(
    <Box>
      { <Tooltip title="Add Properties" arrow placement="bottom">
        <IconButton onClick={() => {
            setIsAddProperty(true); 
            handleOpenDialog();
            }} size="medium">
          <Add style={{color: tableButtonGray}}/>
        </IconButton>
      </Tooltip>}
    </Box>
  )
}

export function getOptionsListForType(type: string) {
  switch(type) {
    case ROAD_TYPE_TYPE_STR:
      return(ROAD_TYPE_OPTIONS)
    case CAPABILITY_TYPE_STR:
      return(CAPABILITY_OPTIONS)
    case USE_CASE_TYPE_STR:
      return(USE_CASE_OPTIONS)
    case PACKAGE_TYPE_STR:
      return(PACKAGE_OPTIONS)
    case MODALITY_TYPE_STR:
      return(MODALITY_OPTIONS)
    case PERCEPTION_DATA_FLOW_TYPE_STR:
      return(PERCEPTION_DATA_FLOW_OPTIONS)
    case SAFETY_TYPE_STR:
      return(SAFETY_OPTIONS)
    default:
      return([])
  }
}

// Data for all categories except Objects is constant and hard-coded
export const ROAD_TYPE_OPTIONS = [
  "Highway", "Urban Dense" ,"Subdivision",
  "Urban Rural"
]

export const CAPABILITY_OPTIONS = [
  "MntHdy", "StopStatnryObj", "StopResume", "Merge - StaticObj", "LCfT - StaticObj", "LCfT", "LCoD", "Merge", "SmplSplit", 
]

export const USE_CASE_OPTIONS = [
  "Case 1", "Case 2"
]

export const PACKAGE_OPTIONS = [
  "None", "UltraCruise"
]

export const MODALITY_OPTIONS = [
  "Radar","Camera", "Camera-PSAT Only", "Camera-YSAT Only","Lidar","Ultrasonics","Radar-Camera Fusion",  
  "Camera-Radar Fusion","Ultrasonics-Camera Fusion", "Camera-Ultrasonics Fusion", "Thermal Camera","Ultrasonics-Radar Fusion",
  "Radar-Ultrasonics Fusion"
]
	 	
export const PERCEPTION_DATA_FLOW_OPTIONS = [
  "DO_FWD", "LL_PNP", "TCD", "DO_360","FS","RM",
  "TSR", "TOFR","TOFR-CIPV","OMPR","Radar-to-Radar Fusion",
  "Single Modality Object", "TLRR","RDFR","TOFR-CIPV (LVM)","Object Tracking"
]

export const SAFETY_OPTIONS = [
  "QM", "QM (S2 E3 C1)", "ASIL A (S2 E3 C2)", "ASIL A (S3 E3 C1)", "ASIL A",
  "ASIL B (S2 E3 C3)", "ASIL B (S3 E3 C2)", "ASIL B", "ASIL C (S3 E3 C3)",
  "ASIL C", "ASIL D"
]

export const LOADER_WRAPPER_STYLE = {marginBottom: 30}

export { datasetSaveData, downloadDatasetAsCSV, drivingScenarioDetailsTable, fetchDatasetDataEndpoint, fetchDatasetDataEndpointFullURL, requiredPropertiesDefault, runAnalysisOnDataset, setBenchmarkSettingsUrl, fetchRwoDataEndpoint }
